/**
 * Risks (cmp_threat) on the 5x5 probability x impact grid, and the measures that mitigate each
 * risk (cmp_measure rows, coreBOS ThreatsMeasures) with their implementation status. The grid is
 * the same reference/risk_matrix.json the risk_level decision table is generated from, so a
 * cell's colour and the derived risk level cannot disagree.
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { useOrgScope } from "@evo-flywheel/ui";
import { Card, Col, Empty, Progress, Row, Space, Statistic, Table, Tooltip, Typography } from "antd";
import { StatusTag } from "../fields";

interface Level { index: number; label: string; color?: string; bg?: string }
interface Engine { probability: Level[]; impact: Level[]; risk: Level[]; grid: number[][] }
const matrix = Object.values(import.meta.glob("../../../reference/risk_matrix.json", { eager: true, import: "default" }))[0] as Record<string, Engine>;
export const ENGINE: Engine = matrix.standard;

export function riskLevel(probability: unknown, impact: unknown): Level | undefined {
  const p = ENGINE.probability.findIndex((x) => x.label === probability);
  const i = ENGINE.impact.findIndex((x) => x.label === impact);
  return p < 0 || i < 0 ? undefined : ENGINE.risk[ENGINE.grid[p][i]];
}

export const RiskHeatmap: React.FC = () => {
  const scope = useOrgScope("cmp_threat");
  const { data, isLoading } = useList({ resource: "cmp_threat", filters: scope.filters, pagination: { pageSize: 1000 } });
  const risks = (data?.data ?? []) as Array<Record<string, unknown>>;
  const cells = useMemo(() => {
    const m = new Map<string, Array<Record<string, unknown>>>();
    for (const r of risks) {
      const k = `${r.importance_level}|${r.thriskscore}`;
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return m;
  }, [risks]);
  const unscored = risks.filter((r) => !riskLevel(r.importance_level, r.thriskscore)).length;
  return <Card size="small" loading={isLoading} style={{ marginBottom: 16 }}
    title="Matrice dei rischi (probabilità × impatto)">
    <table className="cmp-heatmap">
      <tbody>
        {[...ENGINE.probability].reverse().map((p) => <tr key={p.label}>
          <th>{p.label}</th>
          {ENGINE.impact.map((i) => {
            const lvl = ENGINE.risk[ENGINE.grid[p.index][i.index]];
            const here = cells.get(`${p.label}|${i.label}`) ?? [];
            return <td key={i.label} style={{ background: lvl.bg, borderColor: lvl.color }}>
              <Tooltip title={here.length ? here.map((r) => String(r.threatsname ?? r.id)).join(", ") : lvl.label}>
                <span style={{ color: lvl.color, fontWeight: here.length ? 700 : 400 }}>{here.length || ""}</span>
              </Tooltip>
            </td>;
          })}
        </tr>)}
        <tr><th />{ENGINE.impact.map((i) => <th key={i.label}>{i.label}</th>)}</tr>
      </tbody>
    </table>
    {unscored > 0 && <Typography.Text type="secondary">{unscored} rischi senza probabilità o impatto: non compaiono nella matrice.</Typography.Text>}
  </Card>;
};

/** On a risk's page: its measures with status, and the share implemented. */
export const RiskMeasures: React.FC<{ record: Record<string, unknown> }> = ({ record }) => {
  const navigate = useNavigate();
  const id = String(record.id);
  const { data, isLoading } = useList({ resource: "cmp_measure", filters: [{ field: "tmlinktothreats", operator: "eq", value: id }], pagination: { pageSize: 500 } });
  const rows = (data?.data ?? []) as Array<Record<string, unknown>>;
  const lvl = riskLevel(record.importance_level, record.thriskscore);
  const done = rows.filter((r) => r.implementation_status === "Implemented").length;
  return <Card size="small" style={{ marginTop: 16 }} loading={isLoading}
    title={<Space>Misure di mitigazione {lvl && <span style={{ color: lvl.color, fontWeight: 600 }}>· rischio {lvl.label}</span>}</Space>}>
    {rows.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna misura collegata" /> : <>
      <Progress percent={Math.round((done / rows.length) * 100)} format={() => `${done}/${rows.length} implementate`} />
      <Table size="small" rowKey={(r) => String(r.id)} dataSource={rows} pagination={false} style={{ marginTop: 8 }}
        onRow={(r) => ({ onClick: () => navigate(`/cmp_measure/show/${encodeURIComponent(String(r.id))}`), style: { cursor: "pointer" } })}
        columns={[
          { title: "Codice", dataIndex: "measure_id", width: 90 },
          { title: "Misura", render: (_, r) => String(r.threatsmeasurename ?? r.measure_name ?? r.id) },
          { title: "Ambito", dataIndex: "measure_category" },
          { title: "Stato", dataIndex: "implementation_status", render: (v) => v ? <StatusTag value={String(v)} /> : "—" },
        ]} />
    </>}
  </Card>;
};

/** Measures list header: coverage across every risk (source product's "Copertura Misure"). */
export const MeasuresCoverage: React.FC = () => {
  const scope = useOrgScope("cmp_measure"); // measures follow their threat's company (ADR-037)
  const { data, isLoading } = useList({ resource: "cmp_measure", filters: scope.filters, pagination: { pageSize: 2000 } });
  const rows = (data?.data ?? []) as Array<Record<string, unknown>>;
  const n = (s: string) => rows.filter((r) => r.implementation_status === s).length;
  const impl = n("Implemented"), prog = n("In_Progress"), tbd = rows.length - impl - prog;
  const pct = rows.length ? Math.round(((impl + prog * 0.5) / rows.length) * 100) : 0;
  return <Card size="small" loading={isLoading} style={{ marginBottom: 16 }}>
    <Row gutter={16} align="middle">
      <Col span={6}><Progress type="dashboard" size={90} percent={pct} status={pct >= 70 ? "success" : "normal"} /><div><Typography.Text type="secondary">Copertura (in corso = ½)</Typography.Text></div></Col>
      <Col span={6}><Statistic title="Implementate" value={impl} valueStyle={{ color: "#389e0d" }} /></Col>
      <Col span={6}><Statistic title="In corso" value={prog} valueStyle={{ color: "#d48806" }} /></Col>
      <Col span={6}><Statistic title="Da valutare (gap)" value={tbd} valueStyle={{ color: tbd ? "#cf1322" : undefined }} /></Col>
    </Row>
  </Card>;
};
